import React, { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import UserAvatar from "../../common/UserAvatar/UserAvatar";
import "./PastEventsTab.css";

// Super-admin review tab. Lists past events with filter-by-captain
// so the admin can answer "who hosted what last month" without
// digging through Edit Events or DDB. Data source: the existing
// editableSocialEvents query — its sinceDate / beforeDate params
// already cover an arbitrary past window. Captain filter source:
// publicCaptains, joined to event.hosts client-side.
//
// Why client-side filter:
// - The captain showcase is small (~30 captains).
// - The event list inside a window is small (a few hundred at most).
// - DDB has no GSI on hosts. A server-side filter would just be the
//   same scan + filter — pushing it client-side keeps the round
//   trip count low and lets us flip filters instantly.

const PAST_EVENTS_QUERY = gql`
  query PastEventsForReview($sinceDate: String!, $beforeDate: String!) {
    editableSocialEvents(
      sinceDate: $sinceDate
      beforeDate: $beforeDate
      limit: 500
    ) {
      id
      title
      date
      time
      location
      generalpublic
      status
      createdBy
      hosts
    }
  }
`;

const CAPTAINS_QUERY = gql`
  query PastEventsCaptainFilter {
    publicCaptains {
      id
      displayName
      profileImgKey
    }
  }
`;

const RANGE_PRESETS = [
  { id: "30", label: "Last 30 days", days: 30 },
  { id: "90", label: "Last 90 days", days: 90 },
  { id: "ytd", label: "Year to date", ytd: true },
  { id: "365", label: "Last 365 days", days: 365 },
];

const todayYmd = () => new Date().toISOString().slice(0, 10);

const daysAgoYmd = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

const startOfYearYmd = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-01-01`;
};

const resolveRange = (rangeId, customStart, customEnd) => {
  if (rangeId === "custom") {
    return { sinceDate: customStart, beforeDate: customEnd };
  }
  const preset = RANGE_PRESETS.find((p) => p.id === rangeId);
  if (!preset) return { sinceDate: daysAgoYmd(30), beforeDate: todayYmd() };
  if (preset.ytd) {
    return { sinceDate: startOfYearYmd(), beforeDate: todayYmd() };
  }
  return {
    sinceDate: daysAgoYmd(preset.days),
    beforeDate: todayYmd(),
  };
};

const formatYmd = (raw) => {
  if (typeof raw !== "string" || !raw) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!m) return raw;
  const d = new Date(`${raw.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const PastEventsTab = () => {
  const [rangeId, setRangeId] = useState("30");
  const [customStart, setCustomStart] = useState(daysAgoYmd(30));
  const [customEnd, setCustomEnd] = useState(todayYmd());
  const [captainId, setCaptainId] = useState(""); // "" = all
  const [search, setSearch] = useState("");

  const { sinceDate, beforeDate } = useMemo(
    () => resolveRange(rangeId, customStart, customEnd),
    [rangeId, customStart, customEnd]
  );

  const { data, loading, error, refetch } = useQuery(PAST_EVENTS_QUERY, {
    variables: { sinceDate, beforeDate },
    fetchPolicy: "network-only",
  });
  const { data: capData } = useQuery(CAPTAINS_QUERY, {
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    refetch({ sinceDate, beforeDate });
  }, [sinceDate, beforeDate, refetch]);

  const captains = capData?.publicCaptains || [];
  const captainsById = useMemo(() => {
    const m = new Map();
    for (const c of captains) m.set(c.id, c);
    return m;
  }, [captains]);

  const events = data?.editableSocialEvents || [];

  const filtered = useMemo(() => {
    let out = events.slice();
    // Date range from the query already bounds the set; the public
    // events / editableSocialEvents resolver is upcoming-and-recent
    // shaped though, so add an explicit "in the past" filter so
    // future events from inside the window don't show up.
    const cutoff = todayYmd();
    out = out.filter((e) => {
      const ymd = (e.id || "").slice(0, 10);
      return ymd && ymd <= cutoff;
    });
    if (captainId) {
      const cap = captainsById.get(captainId);
      if (cap) {
        const target = String(cap.displayName || "").trim().toLowerCase();
        out = out.filter((e) =>
          (e.hosts || []).some(
            (h) => String(h || "").trim().toLowerCase() === target
          )
        );
      }
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (e) =>
          (e.title || "").toLowerCase().includes(q) ||
          (e.location || "").toLowerCase().includes(q) ||
          (e.hosts || []).some((h) => String(h).toLowerCase().includes(q))
      );
    }
    // Newest first.
    out.sort((a, b) => (b.id || "").localeCompare(a.id || ""));
    return out;
  }, [events, captainId, captainsById, search]);

  // Aggregate the hosted count per captain across the filtered set
  // so the admin can see "Justin hosted 4 events" at a glance.
  const captainTotals = useMemo(() => {
    const counts = new Map();
    for (const e of events) {
      for (const h of e.hosts || []) {
        const key = String(h || "").trim().toLowerCase();
        if (!key) continue;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    return captains
      .map((c) => ({
        ...c,
        count: counts.get(String(c.displayName || "").toLowerCase()) || 0,
      }))
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [events, captains]);

  return (
    <div className="past-events">
      <div className="past-events__header">
        <h2 className="past-events__title">Past events</h2>
      </div>
      <p className="past-events__hint">
        Review who hosted what across a date range. Use this to drive
        captain perks / incentives. Source: every event whose date sits
        in the window AND is on or before today.
      </p>

      <div className="past-events__filters">
        <label className="past-events__filter">
          Range
          <select
            value={rangeId}
            onChange={(e) => setRangeId(e.target.value)}
          >
            {RANGE_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
            <option value="custom">Custom…</option>
          </select>
        </label>
        {rangeId === "custom" && (
          <>
            <label className="past-events__filter">
              From
              <input
                type="date"
                value={customStart}
                max={customEnd}
                onChange={(e) => setCustomStart(e.target.value)}
              />
            </label>
            <label className="past-events__filter">
              To
              <input
                type="date"
                value={customEnd}
                min={customStart}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </label>
          </>
        )}
        <label className="past-events__filter">
          Captain
          <select
            value={captainId}
            onChange={(e) => setCaptainId(e.target.value)}
          >
            <option value="">All captains</option>
            {captains.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="past-events__filter past-events__filter--search">
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="title, location, host…"
          />
        </label>
      </div>

      {error && (
        <div className="past-events__error">
          Error loading events: {error.message}
        </div>
      )}

      {captainTotals.length > 0 && !captainId && (
        <div className="past-events__totals">
          {captainTotals.map((c) => (
            <button
              type="button"
              key={c.id}
              className="past-events__total-chip"
              onClick={() => setCaptainId(c.id)}
              title={`Filter to ${c.displayName}`}
            >
              <UserAvatar
                name={c.displayName}
                imgKey={c.profileImgKey}
                size={24}
              />
              <span className="past-events__total-name">{c.displayName}</span>
              <span className="past-events__total-count">{c.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="past-events__list-meta">
        {loading
          ? "Loading…"
          : `${filtered.length} event${filtered.length === 1 ? "" : "s"} in this window`}
        {captainId && (
          <button
            type="button"
            className="past-events__clear-filter"
            onClick={() => setCaptainId("")}
          >
            Clear captain filter
          </button>
        )}
      </div>

      <ul className="past-events__list">
        {!loading && filtered.length === 0 && (
          <li className="past-events__empty">
            No past events match these filters.
          </li>
        )}
        {filtered.map((e) => (
          <li key={e.id} className="past-events__row">
            <div className="past-events__row-date">
              {formatYmd(e.id)}
              {e.time && (
                <span className="past-events__row-time">{e.time}</span>
              )}
            </div>
            <div className="past-events__row-main">
              <div className="past-events__row-title">
                {e.title || "(untitled)"}
                {!e.generalpublic && (
                  <span className="past-events__row-private">private</span>
                )}
                {e.status && e.status !== "approved" && (
                  <span className="past-events__row-status">{e.status}</span>
                )}
              </div>
              {e.location && (
                <div className="past-events__row-location">
                  <span role="img" aria-label="location">📍</span>{" "}
                  {e.location}
                </div>
              )}
              {Array.isArray(e.hosts) && e.hosts.length > 0 && (
                <div className="past-events__row-hosts">
                  {e.hosts
                    .filter((h) => typeof h === "string" && !/@/.test(h))
                    .map((h, idx) => (
                      <span
                        key={`${e.id}-host-${idx}`}
                        className="past-events__row-host"
                      >
                        {h}
                      </span>
                    ))}
                </div>
              )}
            </div>
            {e.createdBy && (
              <div className="past-events__row-created-by" title="createdBy">
                {e.createdBy}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PastEventsTab;
